import { useEffect, useState } from 'react';
import { getDeckStats } from '../../api/decks.service';
import { getTextStats } from '../../api/texts.service';
import Loader from '../Loader';
import Modal from '../Modal';
import TableComponent from '../TableComponent';
import styles from './CatalogStatsModal.module.sass';

const formatTime = (totalSeconds) => {
    const num = Number(totalSeconds);
    if (!num || Number.isNaN(num) || num < 0) return '0';
    const hours = Math.floor(num / 3600);
    const minutes = Math.floor((num % 3600) / 60);
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);
    return parts.join(' ');
};

const formatDate = (date) => {
    if (!date) return '-';
    const d = new Date(date);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const CatalogStatsModal = ({ visible, onClose, item }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [nextPageHash, setNextPageHash] = useState('');
    const [data, setData] = useState([]);
    const [statsTitle, setStatsTitle] = useState('');
    const [error, setError] = useState(null);

    const isDeck = item?.type === 'deck';
    const columns = [
        { header: 'User', accessor: 'user' },
        { header: 'Level', accessor: 'level' },
        { header: isDeck ? 'Time spent' : 'Reading time', accessor: 'timespent' },
        { header: isDeck ? 'Known Cards' : 'Read %', accessor: 'playedCards' },
        ...(isDeck ? [{ header: 'Unknown', accessor: 'unknownCards' }] : []),
        { header: 'Last played', accessor: 'lastPlayed' },
    ];

    const formatData = (items) => {
        const deckTotal = isDeck ? (item?.contentCount ?? 0) : null;
        return items?.map((d) => {
            const total = d?.totalCards ?? deckTotal ?? 0;
            const known = d?.playedCards ?? 0;
            const unknown = isDeck ? (d?.unknownCards ?? Math.max(0, total - known)) : null;
            return {
                user: {
                    data: d?.user?.name,
                    dataComponent: (
                        <div>
                            <div style={{ fontWeight: 800, color: 'inherit' }}>{d?.user?.name || 'Unknown User'}</div>
                            <div style={{ fontSize: 14, color: 'gray' }}>{d?.user?.email || '-'}</div>
                        </div>
                    )
                },
                level: {
                    data: d?.user?.onboard?.level,
                    dataComponent: d?.user?.onboard?.level || '-'
                },
                timespent: {
                    data: d?.totalPlayed,
                    dataComponent: formatTime(d?.totalPlayed ?? 0)
                },
                playedCards: {
                    data: d?.playedCards,
                    dataComponent: isDeck ? (
                        <div>
                            <span style={{ fontWeight: 600 }}>{known}</span>
                            {total > 0 ? (
                                <span style={{ fontSize: 14, color: 'gray' }}> of {total} cards</span>
                            ) : (
                                <span style={{ fontSize: 14, color: 'gray' }}> cards</span>
                            )}
                        </div>
                    ) : (
                        <span style={{ fontWeight: 600 }}>{typeof known === 'number' ? Number(known).toFixed(2) : known}%</span>
                    )
                },
                ...(isDeck ? {
                    unknownCards: {
                        data: unknown,
                        dataComponent: <span style={{ fontWeight: 600 }}>{unknown}</span>
                    }
                } : {}),
                lastPlayed: {
                    data: d?.lastPlayed,
                    dataComponent: formatDate(d?.lastPlayed)
                },
            };
        });
    };

    const fetchStats = async (isLoadMore = false, searchQuery = '') => {
        if (!item || !visible) return;
        
        try {
            setError(null);
            if (isLoadMore) {
                setIsLoadingMore(true);
            } else if (!searchQuery) {
                setIsLoading(true);
                setData([]);
            }

            const sourceId = item.sourceId;
            let response;
            
            if (sourceId) {
                const params = { 
                    _id: sourceId, 
                    search: searchQuery, 
                    ...(isLoadMore && nextPageHash ? { nextPageHash } : {}), 
                    take: 20 
                };
                if (item.type === 'deck') {
                    response = await getDeckStats(params);
                } else if (item.type === 'text') {
                    response = await getTextStats(params);
                } else {
                    response = { data: { results: [], pagination: {} } };
                }
            } else {
                response = { data: { results: [], pagination: {} } };
            }

            // API returns { results, pagination } at the top level
            if (response?.response?.status >= 400) {
                setError(response?.response?.data?.message || 'Error loading stats');
                if (!isLoadMore) setData([]);
            } else {
                const payload = response?.data ?? response;
                const results = payload?.results ?? [];
                const pagination = payload?.pagination ?? {};

                if (results?.length > 0) {
                    const formatted = formatData(results);
                    if (isLoadMore) {
                        setData((prev) => [...prev, ...formatted]);
                    } else {
                        setData(formatted);
                    }
                    setNextPageHash(pagination?.nextPageHash || '');
                } else if (!isLoadMore) {
                    setData([]);
                    setNextPageHash('');
                }
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
            setError(err?.response?.data?.message || 'Error loading stats');
            if (!isLoadMore) setData([]);
        } finally {
            if (isLoadMore) {
                setIsLoadingMore(false);
            } else {
                setIsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (visible && item) {
            setStatsTitle(item.title);
            fetchStats();
        } else {
            setData([]);
            setNextPageHash('');
            setError(null);
            setIsLoading(true);
        }
    }, [visible, item]);

    const handleSearchUser = async (e) => {
        await fetchStats(false, e);
    };

    const handleLoadMoreUsers = async () => {
        if (nextPageHash && !isLoadingMore) {
            await fetchStats(true);
        }
    };

    const isEmpty = !isLoading && data.length === 0 && !error;

    return (
        <Modal visible={visible} onClose={onClose} outerClassName={styles.statsModalOuter}>
            <div className={styles.statsModalContent}>
                <div className={styles.statsModalHeader}>
                    <h2 className={styles.statsModalTitle}>Stats - {statsTitle}</h2>
                    {item?.type && (
                        <span className={styles.statsModalBadge}>{item.type === 'deck' ? 'Deck' : 'Text'}</span>
                    )}
                </div>
                {isLoading ? (
                    <div className={styles.statsModalLoader}>
                        <Loader />
                    </div>
                ) : error ? (
                    <div className={styles.statsModalEmpty}>
                        <p className={styles.statsModalEmptyText}>{error}</p>
                    </div>
                ) : isEmpty ? (
                    <div className={styles.statsModalEmpty}>
                        <p className={styles.statsModalEmptyText}>No user stats yet for this item.</p>
                    </div>
                ) : (
                    <div className={styles.tableContainer}>
                        <TableComponent
                            data={data}
                            columns={columns}
                            handleSearchAPI={handleSearchUser}
                            onPageLoad={handleLoadMoreUsers}
                            searchPlaceholder="Search user..."
                            className="w-full"
                            embedded
                            isLoadingMore={isLoadingMore}
                            hasMore={!!nextPageHash}
                        />
                        {isLoadingMore && (
                            <div className={styles.statsModalLoaderMore}>
                                <Loader />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default CatalogStatsModal;
